import React, { useState } from "react";
import { Button } from "../../../components/tailwind";
import { useHandleClickOutside } from "../../../utils/reactCustomHooks";
import { CHAT_TOOLS, isChatToolSupported } from "./chatTools";

type Props = {
  openFilePicker: () => void;
  selectedTools: string[];
  onToggleTool: (toolId: string) => void;
  disabled?: boolean;
  /** Model's GPTModel.supported_parameters; drives per-tool availability. */
  supportedParameters?: readonly string[] | null;
};

export const ChatAddMenu: React.FC<Props> = ({
  openFilePicker,
  selectedTools,
  onToggleTool,
  disabled = false,
  supportedParameters,
}) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);

  useHandleClickOutside(
    () => {
      setMenuOpen(false);
      setToolsOpen(false);
    },
    [menuOpen],
    rootRef
  );

  const close = (): void => {
    setMenuOpen(false);
    setToolsOpen(false);
  };

  const anyToolSupported = CHAT_TOOLS.some((tool) =>
    isChatToolSupported(tool, supportedParameters)
  );

  return (
    <div ref={rootRef} className="tw-relative">
      <Button
        onClick={(): void => {
          setMenuOpen((open) => !open);
          setToolsOpen(false);
        }}
        type="button"
        variant="secondary-alt"
        data-testid="chat-add-button"
        compact="very"
        borderless
        iconOnly
        disabled={disabled}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
      >
        <i className="material-icons tw-text-md tw-w-5">add</i>
      </Button>
      {menuOpen && (
        <div
          role="menu"
          data-testid="chat-add-menu"
          className={[
            "txu-simple-menu",
            "tw-absolute",
            "tw-bottom-full",
            "tw-left-0",
            "tw-z-30",
            "tw-mb-1",
            "tw-min-w-[11rem]",
            "tw-rounded-md",
            "tw-bg-white",
            "tw-p-1",
            "tw-shadow-lg",
            "tw-outline",
            "tw-outline-1",
            "tw-outline-gray-200",
          ].join(" ")}
        >
          <button
            type="button"
            role="menuitem"
            data-testid="chat-upload-files"
            className={[
              "txu-simple-menu-item",
              "tw-flex",
              "tw-w-full",
              "tw-items-center",
              "tw-gap-2",
            ].join(" ")}
            onClick={(): void => {
              close();
              openFilePicker();
            }}
          >
            <i className="material-icons tw-text-lg">upload_file</i>
            Upload files
          </button>
          <div
            className="tw-relative"
            onMouseEnter={(): void => {
              setToolsOpen(true);
            }}
            onMouseLeave={(): void => setToolsOpen(false)}
          >
            <button
              type="button"
              role="menuitem"
              data-testid="chat-tools-menu"
              className={[
                "txu-simple-menu-item",
                "tw-flex",
                "tw-w-full",
                "tw-items-center",
                "tw-justify-between",
                "tw-gap-2",
                !anyToolSupported ? "tw-opacity-50" : "",
              ].join(" ")}
              title={
                anyToolSupported
                  ? undefined
                  : "This model does not support any tools"
              }
              onClick={(): void => {
                setToolsOpen((open) => !open);
              }}
            >
              <span className="tw-flex tw-items-center tw-gap-2">
                <i className="material-icons tw-text-lg">construction</i>
                Tools
              </span>
              <i className="material-icons tw-text-lg">chevron_right</i>
            </button>
            {toolsOpen && (
              <div className="tw-absolute tw-bottom-0 tw-left-full tw-z-10 tw-pl-1">
                <div
                  role="menu"
                  data-testid="chat-tools-submenu"
                  className={[
                    "txu-simple-menu",
                    "tw-min-w-[12rem]",
                    "tw-rounded-md",
                    "tw-bg-white",
                    "tw-p-1",
                    "tw-shadow-lg",
                    "tw-outline",
                    "tw-outline-1",
                    "tw-outline-gray-200",
                  ].join(" ")}
                >
                  {CHAT_TOOLS.map((tool) => {
                    const supported = isChatToolSupported(
                      tool,
                      supportedParameters
                    );
                    const selected = selectedTools.includes(tool.id);
                    return (
                      <button
                        key={tool.id}
                        type="button"
                        role="menuitemcheckbox"
                        aria-checked={selected}
                        aria-disabled={!supported}
                        disabled={!supported}
                        data-testid={`chat-tool-${tool.id}`}
                        title={
                          supported
                            ? undefined
                            : "This model does not support this tool"
                        }
                        className={[
                          "txu-simple-menu-item",
                          "tw-flex",
                          "tw-w-full",
                          "tw-items-center",
                          "tw-justify-between",
                          "tw-gap-2",
                          !supported
                            ? "tw-cursor-not-allowed tw-opacity-50"
                            : "",
                        ].join(" ")}
                        onClick={(): void => {
                          if (!supported) {
                            return;
                          }
                          onToggleTool(tool.id);
                          close();
                        }}
                      >
                        {tool.label}
                        {selected && supported && (
                          <i className="material-icons tw-text-lg">check</i>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
